// ---------------------------------------------------------------------------
// Path constants — use these instead of hardcoded strings to avoid typos.
// ---------------------------------------------------------------------------

export const PATHS = {
  login:        '/login',
  dashboard:    '/dashboard',
  transactions: '/transactions',
  resumen:      '/transactions/resumen',
  movementReceipt: '/transactions/movement',
  sendMoney:    '/send-money',
  balances:     '/balances',
  notifications:'/notifications',
  profile:      '/profile',
  profiles:     '/profiles',
  settings:     '/settings',
  security:     '/settings/security',
  deleteAccount: '/settings/delete-account',
  editAlias:    '/profile/edit-alias',
  help:         '/help',
  legal:        '/legal',
  moreOptions:  '/more-options',
  myAccount:    '/my-account',
  editEmailVerify: '/my-account/edit-email/verify',
  editEmailOtp:    '/my-account/edit-email/otp',
  editEmail:       '/my-account/edit-email',
  editPhoneVerify: '/my-account/edit-phone/verify',
  editPhoneOtp:    '/my-account/edit-phone/otp',
  editPhone:       '/my-account/edit-phone',
  editAddress:     '/my-account/edit-address',
  // Public utility
  resetPassword:       '/resetPassword',
  // Onboarding / registration
  onboarding:          '/onboarding',
  onboardingPersonal:  '/onboarding/personal-account',
  onboardingBusiness:  '/onboarding/professional-account',
  legalPrivacy:        '/onboarding/legal/privacy-policy',
  legalTerms:          '/onboarding/legal/terms-and-conditions',
} as const

/** Ruta al comprobante de un movimiento puntual (recibe el id). */
export const movementReceiptPath = (id: string | number) => `${PATHS.movementReceipt}/${id}`

// ---------------------------------------------------------------------------
// Single source of truth for application routes consumed by Sidebar,
// BottomNav and Header. Add new routes here to have them appear everywhere.
// ---------------------------------------------------------------------------

export interface AppRoute {
  path:        string
  navKey:      string   // i18n key suffix: layout.nav.<navKey>
  titleKey:    string   // i18n key: layout.header.title.<...>
  inSidebar:   boolean
  inBottomNav: boolean
}

export const ROUTES: AppRoute[] = [
  { path: PATHS.dashboard,    navKey: 'dashboard',     titleKey: 'layout.header.title.dashboard',    inSidebar: true,  inBottomNav: true  },
  { path: PATHS.transactions, navKey: 'transactions',  titleKey: 'layout.header.title.transactions', inSidebar: true,  inBottomNav: true  },
  { path: PATHS.sendMoney,    navKey: 'sendMoney',     titleKey: 'layout.header.title.sendMoney',    inSidebar: true,  inBottomNav: true  },
  { path: PATHS.balances,     navKey: 'balances',      titleKey: 'layout.header.title.balances',     inSidebar: true,  inBottomNav: true  },
  // /profile sigue existiendo y accesible directo por URL, pero ya no
  // aparece en el sidebar — el acceso a la cuenta del usuario ahora vive
  // dentro de "Mas opciones -> Mi cuenta".
  { path: PATHS.profile,      navKey: 'profile',       titleKey: 'layout.header.title.profile',      inSidebar: false, inBottomNav: false },
  { path: PATHS.moreOptions,  navKey: 'moreOptions',   titleKey: 'layout.header.title.moreOptions',  inSidebar: true,  inBottomNav: true  },
  { path: PATHS.notifications,navKey: 'notifications', titleKey: 'layout.header.title.notifications',inSidebar: false, inBottomNav: false },
  { path: PATHS.settings,     navKey: 'settings',      titleKey: 'layout.header.title.settings',     inSidebar: false, inBottomNav: false },
  { path: PATHS.security,      navKey: 'security',       titleKey: 'layout.header.title.security',      inSidebar: false, inBottomNav: false },
  { path: PATHS.deleteAccount, navKey: 'deleteAccount',  titleKey: 'layout.header.title.deleteAccount', inSidebar: false, inBottomNav: false },
  { path: PATHS.editAlias,   navKey: 'editAlias',     titleKey: 'layout.header.title.editAlias',    inSidebar: false, inBottomNav: false },
  { path: PATHS.profiles,     navKey: 'profiles',      titleKey: 'layout.header.title.profiles',     inSidebar: false, inBottomNav: false },
  { path: PATHS.help,         navKey: 'help',           titleKey: 'layout.header.title.help',        inSidebar: false, inBottomNav: false },
  { path: PATHS.legal,        navKey: 'legal',          titleKey: 'layout.header.title.legal',       inSidebar: false, inBottomNav: false },
  { path: PATHS.resumen,      navKey: 'resumen',        titleKey: 'layout.header.title.resumen',          inSidebar: false, inBottomNav: false },
  { path: PATHS.movementReceipt, navKey: 'movementReceipt', titleKey: 'layout.header.title.movementReceipt', inSidebar: false, inBottomNav: false },
  { path: PATHS.myAccount,    navKey: 'myAccount',      titleKey: 'layout.header.title.myAccount',   inSidebar: false, inBottomNav: false },
  { path: PATHS.editEmailVerify, navKey: 'editEmail',  titleKey: 'layout.header.title.editEmail',    inSidebar: false, inBottomNav: false },
  { path: PATHS.editEmailOtp,    navKey: 'editEmail',  titleKey: 'layout.header.title.editEmail',    inSidebar: false, inBottomNav: false },
  { path: PATHS.editEmail,       navKey: 'editEmail',  titleKey: 'layout.header.title.editEmail',    inSidebar: false, inBottomNav: false },
  { path: PATHS.editPhoneVerify, navKey: 'editPhone',  titleKey: 'layout.header.title.editPhone',    inSidebar: false, inBottomNav: false },
  { path: PATHS.editPhoneOtp,    navKey: 'editPhone',  titleKey: 'layout.header.title.editPhone',    inSidebar: false, inBottomNav: false },
  { path: PATHS.editPhone,       navKey: 'editPhone',  titleKey: 'layout.header.title.editPhone',    inSidebar: false, inBottomNav: false },
  { path: PATHS.editAddress,     navKey: 'editAddress',titleKey: 'layout.header.title.editAddress',  inSidebar: false, inBottomNav: false },
]

// Sub-routes that count as "Más opciones" for the sidebar/bottom-nav
// highlight. These pages live at the top level but are reached through the
// MoreOptionsView menu, so the parent entry should stay active when the
// user navigates into them.
const MORE_OPTIONS_CHILDREN = [
  PATHS.settings,
  PATHS.security,
  PATHS.deleteAccount,
  PATHS.help,
  PATHS.legal,
  PATHS.profiles,
  PATHS.myAccount,
  PATHS.notifications,
] as const

// Prefix-match except for /dashboard (exact only) — unified criterion.
// Plus a manual override: any of the MORE_OPTIONS_CHILDREN keeps the
// "Más opciones" entry highlighted.
export function isRouteActive(pathname: string, path: string): boolean {
  if (path === '/dashboard') return pathname === path
  if (pathname === path) return true
  if (pathname.startsWith(path + '/')) return true
  if (
    path === PATHS.moreOptions &&
    MORE_OPTIONS_CHILDREN.some(c => pathname === c || pathname.startsWith(c + '/'))
  ) return true
  return false
}

// Look up the header title key for the current pathname (exact first, then longest prefix).
export function getTitleKey(pathname: string): string {
  // Exact match first
  const exact = ROUTES.find(r => r.path === pathname)
  if (exact) return exact.titleKey
  // Longest prefix match
  const prefix = ROUTES
    .filter(r => pathname.startsWith(r.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0]
  return prefix?.titleKey ?? 'layout.header.title.default'
}
